"use client";

import { InlineInput, SkeletonLine, UserMessage, useStylesConfigValue } from "@health/ui";
import { cn } from "@health/ui/utils";
import { useEffect, useRef } from "react";
import { useAnalyticsContext } from "../analytics/context";
import { useTrack } from "../analytics/useTrack";
import { useTurnTiming } from "../analytics/useTurnTiming";

type Props = {
  input: Record<string, unknown>;
  output?: unknown;
  onSubmit: (result: unknown) => void;
  submitted?: boolean;
  demoMode?: boolean;
  streaming?: boolean;
};

export function InlineTextInput({ input, output, onSubmit, submitted, streaming }: Props) {
  const placeholder = (input.placeholder as string) ?? "Type your answer...";
  const buttonText = (input.button_text as string) ?? "Send";

  // Trace-mode-strip follow-up — emit `latency:user_to_first_widget_ms` when
  // the text-input widget becomes interactable (not streaming, not submitted).
  // Mirrors the ChipsWidget pattern but without the rAF fallback: `InlineInput`
  // owns focus (autoFocus default), so the first render where ready === true
  // IS the patient-felt interactive moment. No extra DOM query needed.
  const didEmitRef = useRef(false);
  const readyForFocus = !streaming && !submitted;
  const { markAnyWidgetReady } = useTurnTiming();
  const analytics = useAnalyticsContext();
  const track = useTrack();
  const traceMode = useStylesConfigValue("traceMode");
  useEffect(() => {
    if (!readyForFocus || didEmitRef.current) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    didEmitRef.current = true;
    const anyResult = markAnyWidgetReady({ widgetType: "text-input" });
    const variantId = analytics?.variantId;
    if (!anyResult || !variantId) return;
    track("latency:user_to_first_widget_ms", {
      durationMs: anyResult.durationMs,
      turnIndex: anyResult.turnIndex,
      trigger: anyResult.trigger,
      widgetType: "text-input",
      capture_source: "browser",
      variantId,
      ...(analytics?.site ? { brand: analytics.site } : {}),
      ...(traceMode === "plan" || traceMode === "disabled" || traceMode === "deliberate"
        ? { traceMode }
        : {}),
    });
  }, [readyForFocus, markAnyWidgetReady, track, analytics?.variantId, analytics?.site, traceMode]);

  if (submitted && output) {
    const result = output as { value: string };
    return (
      <div className="flex justify-end w-full">
        <UserMessage>{result.value}</UserMessage>
      </div>
    );
  }

  return (
    <InlineInput
      placeholder={placeholder}
      submitLabel={buttonText}
      disabled={streaming}
      onSubmit={(value) => onSubmit({ value })}
      className={cn("mt-3", streaming && "animate-pulse")}
      autoFocus={input.autoFocus as boolean | undefined}
      autoFocusInView={input.autoFocusInView as boolean | undefined}
    />
  );
}

/**
 * Loading skeleton mirroring InlineInput — borderless input with a thin
 * underline (no surrounding card). Submit button only appears once the user
 * types so we omit it here.
 */
export function InlineTextInputSkeleton() {
  return (
    <div className="flex flex-col gap-3 w-full sm:max-w-md mt-3">
      <div className="border-b border-border pb-2">
        <SkeletonLine $size="title" $width="lg" />
      </div>
    </div>
  );
}
